/**
 * Staged dial-plan changes (ADR 0032): the review queue for every routing
 * write the platform makes.
 *
 * Stage → review the per-target entry diff → approve (typing the name of
 * any GLOBAL plan — those are shared by every domain on the node) →
 * optionally hold for a maintenance window → apply on explicit click →
 * rollback from the stored snapshot. The platform never applies on its
 * own: when a window opens, the change becomes applicable and a human
 * triggers it, so customers can test in-window before go-live.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  applyChangeSet,
  approveChangeSet,
  cancelChangeSet,
  getChangeSets,
  getNodesConfig,
  rollbackChangeSet,
  scheduleChangeSet,
  stageChangeSet,
  stagePlanDelete,
} from "@pbxmgr/api";
import type { ChangeSetRow, DpsEntry } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner, TextField } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";

const STATUS_TONE: Record<string, BadgeTone> = {
  draft: "unknown",
  approved: "degraded",
  scheduled: "degraded",
  applied: "online",
  failed: "offline",
  rolled_back: "unknown",
  cancelled: "unknown",
};

function errorText(err: unknown): string {
  return err instanceof ApiError ? (err.detail ?? err.message) : "request failed";
}

function when(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function EntryList({ label, entries }: { label: string; entries: DpsEntry[] }): ReactNode {
  if (entries.length === 0) return null;
  return (
    <div className="changeset-entries">
      <strong>{label}</strong>
      <ul>
        {entries.map((e, i) => (
          <li key={i}>
            <code>
              {e.pattern} → {e.replacement}
            </code>{" "}
            (trunk {e.trunk}, pref {e.preference})
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChangeSetCard({ set }: { set: ChangeSetRow }): ReactNode {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["changesets"] });
  };
  const onError = (err: unknown): void => setError(errorText(err));

  const approve = useMutation({
    mutationFn: () =>
      approveChangeSet(
        set.id,
        typed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    onSuccess: invalidate,
    onError,
  });
  const schedule = useMutation({
    mutationFn: () =>
      scheduleChangeSet(
        set.id,
        new Date(windowStart).toISOString(),
        new Date(windowEnd).toISOString(),
      ),
    onSuccess: invalidate,
    onError,
  });
  const apply = useMutation({
    mutationFn: () => applyChangeSet(set.id),
    onSuccess: invalidate,
    onError,
  });
  const rollback = useMutation({
    mutationFn: () => rollbackChangeSet(set.id),
    onSuccess: invalidate,
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => cancelChangeSet(set.id),
    onSuccess: invalidate,
    onError,
  });

  const globals = set.targets.filter((t) => t.global && !t.blocked).map((t) => t.name);
  const pendingState = ["draft", "approved", "scheduled"].includes(set.status);

  return (
    <div className="changeset-card">
      <div className="changeset-head">
        <strong>
          #{set.id} — {set.domain}
        </strong>
        <Badge tone={STATUS_TONE[set.status] ?? "unknown"}>{set.status}</Badge>
        {set.window_start ? (
          <span className="ui-empty">
            window {when(set.window_start)} → {when(set.window_end)}
          </span>
        ) : null}
      </div>

      {set.targets.map((t) => {
        const key = `${t.kind}:${t.id}`;
        const d = set.diff[key];
        const result = set.results?.[key];
        return (
          <div key={key} className="changeset-target">
            <div>
              <Badge tone={t.blocked ? "degraded" : "unknown"}>{t.kind}</Badge>{" "}
              <strong>{t.name}</strong> {t.global ? <Badge tone="offline">GLOBAL</Badge> : null}
              {result ? (
                <Badge tone={result.status === "ok" ? "online" : "degraded"}>
                  {result.status}
                </Badge>
              ) : null}
            </div>
            {t.blocked ? <p className="ui-empty">{t.blocked}</p> : null}
            {result?.detail ? <p className="ui-empty">{result.detail}</p> : null}
            {d?.delete_plan ? (
              <EntryList
                label="Plan will be DELETED (entries snapshotted for rollback)"
                entries={d.entries ?? []}
              />
            ) : d ? (
              <>
                <EntryList label="Add" entries={d.add ?? []} />
                <EntryList label="Remove" entries={d.remove ?? []} />
                <EntryList label="Foreign (blocking)" entries={d.foreign ?? []} />
              </>
            ) : null}
          </div>
        );
      })}

      <div className="pairing-actions">
        {set.status === "draft" ? (
          <>
            {globals.length > 0 ? (
              <TextField
                id={`typed-${set.id}`}
                label={`Type global plan name(s) to confirm: ${globals.join(", ")}`}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            ) : null}
            <Button disabled={approve.isPending} onClick={() => approve.mutate()}>
              Approve
            </Button>
          </>
        ) : null}
        {set.status === "approved" ? (
          <>
            <TextField
              id={`ws-${set.id}`}
              label="Window start"
              type="datetime-local"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
            />
            <TextField
              id={`we-${set.id}`}
              label="Window end"
              type="datetime-local"
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={schedule.isPending || !windowStart || !windowEnd}
              onClick={() => schedule.mutate()}
            >
              Schedule window
            </Button>
            <Button disabled={apply.isPending} onClick={() => apply.mutate()}>
              Apply now
            </Button>
          </>
        ) : null}
        {set.status === "scheduled" ? (
          <Button disabled={apply.isPending} onClick={() => apply.mutate()}>
            Apply (in window)
          </Button>
        ) : null}
        {set.status === "applied" || set.status === "failed" ? (
          <Button
            variant="secondary"
            disabled={rollback.isPending}
            onClick={() => rollback.mutate()}
          >
            Roll back to snapshot
          </Button>
        ) : null}
        {pendingState ? (
          <Button variant="secondary" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            Cancel
          </Button>
        ) : null}
      </div>
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

export function ChangeSetsPage(): ReactNode {
  const canManage = usePermission("dialplan.manage");
  const qc = useQueryClient();
  const sets = useQuery({ queryKey: ["changesets"], queryFn: getChangeSets });
  const nodes = useQuery({ queryKey: ["nodes"], queryFn: getNodesConfig, enabled: canManage });
  const [nodeId, setNodeId] = useState("");
  const [domain, setDomain] = useState("");
  const [deletePlanName, setDeletePlanName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const stageDelete = useMutation({
    mutationFn: () => stagePlanDelete(Number(nodeId), domain.trim(), deletePlanName.trim()),
    onSuccess: () => {
      setError(null);
      setDeletePlanName("");
      void qc.invalidateQueries({ queryKey: ["changesets"] });
    },
    onError: (err) => setError(errorText(err)),
  });
  const stage = useMutation({
    mutationFn: () => stageChangeSet(Number(nodeId), domain.trim()),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["changesets"] });
    },
    onError: (err) => setError(errorText(err)),
  });

  return (
    <div>
      <div className="dash__head">
        <h1>Routing changes</h1>
      </div>

      <Panel title="Stage a change">
        <p>
          Staging computes what would change — per plan, per entry — and takes a full backup
          snapshot. Nothing touches a PBX until an approved change is explicitly applied,
          optionally inside a scheduled maintenance window.
        </p>
        {canManage ? (
          <div className="pairing-actions">
            <div className="ui-field">
              <label htmlFor="stage-node">Node</label>
              <select
                id="stage-node"
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
              >
                <option value="">choose…</option>
                {(nodes.data?.items ?? []).map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </div>
            <TextField
              id="stage-domain"
              label="Domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
            <Button disabled={stage.isPending || !nodeId || !domain} onClick={() => stage.mutate()}>
              {stage.isPending ? "Staging…" : "Stage change"}
            </Button>
            <TextField
              id="stage-delete-plan"
              label="…or stage a plan DELETION (unused local plan name)"
              value={deletePlanName}
              onChange={(e) => setDeletePlanName(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={stageDelete.isPending || !nodeId || !domain || !deletePlanName}
              onClick={() => stageDelete.mutate()}
            >
              Stage deletion
            </Button>
          </div>
        ) : (
          <p className="ui-empty">dialplan.manage required to stage or apply changes.</p>
        )}
        {error ? <Alert>{error}</Alert> : null}
      </Panel>

      <Panel title={`Change sets (${sets.data?.items.length ?? 0})`}>
        {sets.isLoading ? (
          <Spinner />
        ) : (sets.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">Nothing staged yet.</div>
        ) : (
          sets.data?.items.map((s) => <ChangeSetCard key={s.id} set={s} />)
        )}
      </Panel>
    </div>
  );
}
